import * as React from 'react'
import { Slot } from '@radix-ui/react-slot'
import { cva, type VariantProps } from 'class-variance-authority'

import { cn } from '~/utils/misc.tsx'

export type ButtonSize =
  | 'default'
  | 'wide'
  | 'xs-circle'
  | 'circle'
  | 'xs'
  | 'sm'
  | 'lg'
  | 'pill'
  | 'icon'
  | null
  | undefined

const buttonVariants = cva(
  'inline-flex items-center justify-center rounded-md text-sm font-bold ring-offset-cards transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:pointer-events-none disabled:opacity-50',
  {
    variants: {
      variant: {
        orange: 'bg-primary-300 text-white hover:bg-primary-500',
        primary: 'bg-secondary text-white hover:bg-secondary-header', // primary
        cancel: 'bg-gray-700 text-white hover:bg-gray-700/90',
        outline:
          'border border-input bg-white hover:border-secondary focus:border-2 focus:border-secondary focus-visible:ring-0',
        'outline-secondary':
          ' border border-secondary text-secondary bg-white hover:bg-secondary-100 hover:shadow-buttons focus:border-2 focus:border-secondary focus-visible:ring-0',
        // secondary: 'bg-secondary text-white hover:bg-secondary/80',
        ghost: 'hover:bg-gray-100',
        danger: 'bg-danger text-white hover:bg-badge-red-foreground',
        white: 'bg-white text-black hover:bg-gray-300',
        overlay: 'bg-black bg-opacity-30 hover:bg-opacity-100 text-white',
        // link: 'text-primary-text underline-offset-4 hover:underline',
      },
      size: {
        default: 'h-11 px-4 py-2',
        wide: 'px-24 py-5',
        'xs-circle': 'h-7 rounded-full px-1.5',
        circle: 'h-11 rounded-full w-11',
        xs: 'h-7 px-1.5',
        sm: 'h-9 rounded-md px-3',
        lg: 'h-12 rounded-md px-8',
        pill: 'px-12 py-3 leading-3',
        icon: 'h-11 w-11',
      },
    },
    defaultVariants: {
      variant: 'primary',
      size: 'default',
    },
  },
)

export interface ButtonProps
  extends React.ButtonHTMLAttributes<HTMLButtonElement>,
    VariantProps<typeof buttonVariants> {
  asChild?: boolean
}

const Button = React.forwardRef<HTMLButtonElement, ButtonProps>(
  ({ className, variant, size, asChild = false, ...props }, ref) => {
    const Comp = asChild ? Slot : 'button'
    return (
      <Comp
        className={cn(buttonVariants({ variant, size, className }))}
        ref={ref}
        {...props}
      />
    )
  },
)
Button.displayName = 'Button'

export interface ButtonTriggerProps
  extends React.HTMLAttributes<HTMLDivElement>,
    VariantProps<typeof buttonVariants> {}

const ButtonTrigger = React.forwardRef<HTMLDivElement, ButtonTriggerProps>(
  ({ className, variant, size, ...props }, ref) => {
    return (
      <div
        className={cn(
          buttonVariants({ variant, size, className }),
          'cursor-pointer',
        )}
        ref={ref}
        {...props}
      />
    )
  },
)
ButtonTrigger.displayName = 'ButtonTrigger'

export { Button, ButtonTrigger, buttonVariants }
